import React, { useState } from "react";
import { Pencil, Merge, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function CategoryManagerDialog({ open, onOpenChange, categories, counts, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const start = (c) => {
    setEditing(c);
    setValue(c);
  };

  const cancel = () => {
    setEditing(null);
    setValue("");
  };

  const target = value.trim().replace(/\s+/g, " ");
  const willMerge = target && target !== editing && categories.includes(target);

  let saveIcon;
  if (saving) saveIcon = <Loader2 className="h-4 w-4 animate-spin" />;
  else if (willMerge) saveIcon = <Merge className="h-4 w-4" />;
  else saveIcon = <Pencil className="h-4 w-4" />;

  const save = async () => {
    if (target.length < 2) return toast.error("Category needs at least 2 characters.");
    if (target === editing) return cancel();
    setSaving(true);
    try {
      const { data } = await api.patch("/categories", { name: editing, new_name: target });
      toast.success(
        data.merged
          ? `Merged "${data.from}" into "${data.to}" (${data.updated} runbooks moved)`
          : `Renamed "${data.from}" to "${data.to}" (${data.updated} runbooks updated)`
      );
      cancel();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="category-manager-dialog" className="max-w-lg bg-popover">
        <DialogHeader>
          <DialogTitle className="font-display">Manage categories</DialogTitle>
          <DialogDescription>
            Rename a category, or type an existing name to merge it. Every runbook in the category moves with it.
          </DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-border max-h-[50vh] overflow-y-auto tr-scroll">
          {categories.map((c) => (
            <li key={c} data-testid={`category-row-${c}`} className="flex items-center gap-3 py-2.5 text-sm">
              {editing === c ? (
                <>
                  <Input
                    autoFocus
                    data-testid="category-rename-input"
                    value={value}
                    maxLength={40}
                    list="category-manager-options"
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") save();
                      if (e.key === "Escape") cancel();
                    }}
                    className="h-9"
                  />
                  <datalist id="category-manager-options">
                    {categories.filter((x) => x !== c).map((x) => <option key={x} value={x} />)}
                  </datalist>
                  <Button size="sm" onClick={save} disabled={saving} data-testid="category-rename-save" className="gap-1.5 shrink-0">
                    {saveIcon}
                    {willMerge ? "Merge" : "Rename"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancel} disabled={saving} data-testid="category-rename-cancel">
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <span className="flex-1 truncate font-medium">{c}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">{counts[c] || 0} runbooks</span>
                  <Button size="sm" variant="outline" onClick={() => start(c)} data-testid={`category-edit-${c}`} className="gap-1.5">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
